import React from "react";
import * as yup from "yup";
import FlexBox from "components/FlexBox/FlexBox";
import Guard from "components/Guard/Guard";
import Dialog from "components/odl-v2/Dialog/Dialog";
import DialogActionCancel from "components/odl-v2/Dialog/DialogActionCancel";
import DialogActions from "components/odl-v2/Dialog/DialogActions";
import DialogContent from "components/odl-v2/Dialog/DialogContent";
import ApplicationSearchFilters from "./ApplicationSearchFilters";
import ApplicationSearchTable from "./ApplicationSearchTable";
import Spacer from "components/Spacer/Spacer";
import folderImg from "assets/images/folder.svg";
import { ReactComponent as SystemGroupAccessIcon } from "assets/images/system-group-access.svg";
import { Form, Formik } from "formik";
import { usePaginatedData } from "hooks/usePaginatedData";
import { useTranslation } from "react-i18next";
import { useApplicationSearch } from "./functions/useApplicationSearch";
import { css } from "@emotion/react";
import { styled } from "@mui/material/styles";
import { formValidationUtils } from "utils/formValidationUtils";
import { ApplicationSearchFormData } from "models/Application.model";
import { useAccessRequestActions } from "hooks/useAccessRequestActions";
import { useToast } from "hooks/useToast";
import { serviceContainer } from "services/serviceContainer";

const validationSchema = yup.object({
  address: yup
    .string()
    .trim()
    .when("isAdvancedSearch", {
      is: false,
      then: yup.string().trim().required(formValidationUtils.getErrorMessageForRequiredField()),
    }),
  applicationNumber: yup
    .string()
    .trim()
    .when("isAdvancedSearch", {
      is: true,
      then: yup.string().trim().required(formValidationUtils.getErrorMessageForRequiredField()),
    }),
  authorities: yup
    .array()
    .of(yup.number())
    .when("isAdvancedSearch", {
      is: true,
      then: yup
        .array()
        .of(yup.number())
        .required(formValidationUtils.getErrorMessageForRequiredField())
        .min(1, formValidationUtils.getErrorMessageForRequiredField()),
    }),
  isAdvancedSearch: yup.boolean(),
});

type Props = {
  onClose: (isRequestSuccessful: boolean) => void;
};

const AccessRequestDialog: React.FC<Props> = ({ onClose }) => {
  const { t } = useTranslation();
  const { toastError } = useToast();

  const [formValues, setFormValues] = React.useState<ApplicationSearchFormData>({
    applicationNumber: "",
    authorities: [],
    address: "",
    isAdvancedSearch: false,
  });

  const searchApplications = useApplicationSearch(formValues);
  const { data, hasNextPage, loadNextPage, isLoading } = usePaginatedData(searchApplications);
  const { openApplication, requestAccess } = useAccessRequestActions();

  const handleClickRequestAccess = React.useCallback(
    async (applicationId: number) => {
      const logger = serviceContainer.cradle.logger;
      try {
        await requestAccess(applicationId);
        onClose(true);
      } catch (e) {
        logger.error("Participant access request submit failed", e);
        toastError(e);
      }
    },
    [requestAccess, onClose, toastError]
  );

  const handleClickOpenApplication = React.useCallback(
    async (applicationId: number) => {
      const applicationSearchItem = data.find((item) => item.applicationId === applicationId);
      if (!applicationSearchItem) {
        return;
      }

      await openApplication(applicationId, applicationSearchItem.status);
    },
    [openApplication, data]
  );

  return (
    <Dialog
      open={true}
      fullWidth
      data-testid={"AccessRequestDialog"}
      title={
        <FlexBox alignItems={"center"}>
          <SystemGroupAccessIcon width={20} height={20} />
          <Spacer x={4} />
          {t(`Request access to a project`)}
        </FlexBox>
      }
      onClose={() => onClose(false)}
      scroll="body"
    >
      <Formik<ApplicationSearchFormData>
        initialValues={formValues}
        onSubmit={setFormValues}
        onReset={setFormValues}
        validateOnChange={true}
        validationSchema={validationSchema}
      >
        {({ submitCount }) => (
          <StyledDialogContent>
            <Form>
              <ApplicationSearchFilters />
            </Form>

            <Guard condition={submitCount > 0} fallback={<InitialPlaceholder />}>
              <ApplicationSearchTable
                data={data}
                hasNextPage={hasNextPage}
                loadNextPage={loadNextPage}
                isLoading={isLoading}
                onClickOpenApplication={handleClickOpenApplication}
                onClickRequestAccess={handleClickRequestAccess}
              />
            </Guard>
          </StyledDialogContent>
        )}
      </Formik>
      <DialogActions>
        <DialogActionCancel data-testid="CancelButton" onClick={() => onClose(false)}>
          {t(`Cancel`)}
        </DialogActionCancel>
      </DialogActions>
    </Dialog>
  );
};

const InitialPlaceholder: React.FC = () => {
  return (
    <ImageWrapper>
      <StyledImage src={folderImg} alt={"Folder Icon"} />
    </ImageWrapper>
  );
};

const StyledDialogContent = styled(DialogContent)(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    padding: 8px 32px 16px 32px !important;
    height: 507px;
    ${theme.mixins.flexGap("40px")}
  `
);

const ImageWrapper = styled("div")(
  ({ theme }) => css`
    flex: 1 1 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  `
);

const StyledImage = styled("img")(
  ({ theme }) => css`
    width: 212px;
    height: 212px;
  `
);

export default AccessRequestDialog;
